import type {
  ChartConfig,
  ColorScale,
  DivergingPalette,
  SequentialPalette,
} from './chart-types';
import { infoOnce, warnOnce } from '@/hen-ui/utils';

/**
 * Categorical palette for many-series charts. The 12 hues mirror the
 * `--data-viz-series-{a..l}` CSS custom properties published by the
 * hen-ui tailwind plugin (`tailwind.config.ts`), and each entry uses the
 * same hex literal as a `var(...)` fallback. Tenants that override any
 * `--data-viz-series-*` at `:root` (via the partner-theme pipeline)
 * automatically pick up the new value; consumers without an override
 * resolve to the hex below.
 *
 * Order is intentional: slots are arranged so the first N hues stay
 * maximally distinguishable for low-series-count charts (3-, 4-, 5-
 * series cohort comparisons all read cleanly without bespoke color
 * choices). Past 12 series the palette cycles via {@link getDataVizSeriesColor}.
 *
 * Single source of truth for both the auto-fill in chart composites
 * (via {@link withSeriesPaletteDefaults}) and any consumer that wants
 * to address a slot directly. Updating a hex here also requires the
 * matching update in `tailwind.config.ts` so both code paths stay in
 * lockstep.
 */
export const DATA_VIZ_PALETTE: readonly string[] = [
  'var(--data-viz-series-a, #125FAB)',
  'var(--data-viz-series-b, #E58620)',
  'var(--data-viz-series-c, #44994D)',
  'var(--data-viz-series-d, #9982FF)',
  'var(--data-viz-series-e, #5FC4B9)',
  'var(--data-viz-series-f, #C73E64)',
  'var(--data-viz-series-g, #F6BC5F)',
  'var(--data-viz-series-h, #824388)',
  'var(--data-viz-series-i, #A0D96A)',
  'var(--data-viz-series-j, #E27CB8)',
  'var(--data-viz-series-k, #4193EB)',
  'var(--data-viz-series-l, #9CA3B0)',
] as const;

/**
 * Resolve the data-viz palette color for a given series index. Cycles
 * via modulo when `index >= DATA_VIZ_PALETTE.length` — adjacent series
 * past slot `l` reuse the palette from the start (Blue, Orange, Green, …).
 * Cycling is deliberate: a 13-series chart should still render rather
 * than throw, and reusing hues at the tail is a softer failure than
 * a transparent bar.
 *
 * Negative indices are floored to 0 — the algorithm consuming this
 * function is positional (an array index), so a negative value indicates
 * a caller bug and we return the canonical "first" color rather than
 * silently wrapping to the tail.
 */
export function getDataVizSeriesColor(index: number): string {
  if (!Number.isFinite(index) || index < 0) {
    return DATA_VIZ_PALETTE[0];
  }
  return DATA_VIZ_PALETTE[Math.floor(index) % DATA_VIZ_PALETTE.length];
}

/**
 * Return a `ChartConfig` clone with `color` filled in for every series
 * key whose entry is missing or has no `color`/`theme`. The default is
 * resolved from {@link DATA_VIZ_PALETTE} by the key's index in `series[]`,
 * so adjacent legend entries stay maximally distinguishable.
 *
 * Behavior contract:
 * - Entries with an explicit `color` or `theme` are passed through unchanged
 *   (consumers can still override per-series by populating the field).
 * - Entries missing entirely from `config` are created with `{label: key, color}`
 *   so the chart still renders a swatch + legend label rather than a
 *   transparent series. The dev-only `useMissingChartConfigWarning` continues
 *   to flag missing labels so dashboards don't ship raw keys to users.
 * - The original `config` reference is not mutated; a new object is returned.
 *   Composites can therefore call this in render without violating React's
 *   immutable-prop expectations.
 *
 * Order matters: pass the same `series` array the chart will iterate over
 * (Recharts preserves array order for the legend and the bar/line stack).
 * Reordering `series` will reshuffle which hue lands on which key.
 */
export function withSeriesPaletteDefaults(
  config: ChartConfig,
  series: readonly string[],
): ChartConfig {
  const next: ChartConfig = { ...config };
  series.forEach((key, index) => {
    const entry = next[key];
    // Re-fill when the entry is missing OR present-but-color-less. The
    // ChartConfig union allows an entry to declare `theme` instead of
    // `color`; treat that as already-themed and leave it alone.
    const hasResolvedColor =
      entry !== undefined && (entry.color !== undefined || 'theme' in entry);
    if (hasResolvedColor) return;

    next[key] = {
      label: entry?.label ?? key,
      icon: entry?.icon,
      color: getDataVizSeriesColor(index),
    };
  });
  return next;
}

// ────────────────────────────────────────────────────────────────────────────
// Sequential + diverging palette families
// ────────────────────────────────────────────────────────────────────────────

/*
 * Each family below is published as a `var(--data-viz-<...>, #hex)` array so
 * tenants can override individual stops at `:root` (mirroring the
 * `--data-viz-series-*` categorical pattern), with the light-mode hex baked in
 * as the fallback. Dark-mode values are published as `.dark` overrides of the
 * same custom properties in `tailwind.config.ts`, and mirrored in the
 * `*_DARK` arrays below for accessibility verification.
 *
 * Stops are tuned so every (non-neutral) stop clears WCAG ≥3:1 against the
 * surface it renders on (white in light mode, neutral-900 in dark mode) and
 * adjacent stops clear ≥1.5:1, in both modes. Updating a hex here requires the
 * matching update in `tailwind.config.ts` so both code paths stay in lockstep.
 */

/** Sequential blues — ordered light→dark. */
export const SEQUENTIAL_PALETTE_BLUES: readonly string[] = [
  'var(--data-viz-seq-blues-1, #6597C8)',
  'var(--data-viz-seq-blues-2, #2E72B5)',
  'var(--data-viz-seq-blues-3, #0F5090)',
  'var(--data-viz-seq-blues-4, #072543)',
] as const;

/** Sequential greens — ordered light→dark. */
export const SEQUENTIAL_PALETTE_GREENS: readonly string[] = [
  'var(--data-viz-seq-greens-1, #5CA25F)',
  'var(--data-viz-seq-greens-2, #327F35)',
  'var(--data-viz-seq-greens-3, #235A26)',
  'var(--data-viz-seq-greens-4, #102A12)',
] as const;

/** Sequential oranges — ordered light→dark. */
export const SEQUENTIAL_PALETTE_ORANGES: readonly string[] = [
  'var(--data-viz-seq-oranges-1, #D67D1E)',
  'var(--data-viz-seq-oranges-2, #A35F17)',
  'var(--data-viz-seq-oranges-3, #734410)',
  'var(--data-viz-seq-oranges-4, #361F08)',
] as const;

/** Sequential purples — ordered light→dark. */
export const SEQUENTIAL_PALETTE_PURPLES: readonly string[] = [
  'var(--data-viz-seq-purples-1, #AE85B2)',
  'var(--data-viz-seq-purples-2, #925C98)',
  'var(--data-viz-seq-purples-3, #703A76)',
  'var(--data-viz-seq-purples-4, #341B37)',
] as const;

/**
 * Diverging blue↔orange — `[lowArm, neutral, highArm]`. Matches the
 * choropleth diverging direction used by HeatmapChart (orange = low/loss,
 * blue = high/gain). The neutral midpoint is intentionally near-white (the
 * "zero / no-deviation" color) and is exempt from the ≥3:1 contrast rule.
 */
export const DIVERGING_PALETTE_BLUE_ORANGE: readonly string[] = [
  'var(--data-viz-div-blueOrange-low, #C8751C)',
  'var(--data-viz-div-blueOrange-neutral, #F8FAFC)',
  'var(--data-viz-div-blueOrange-high, #568DC3)',
] as const;

/** Diverging purple↔green — `[lowArm, neutral, highArm]` (colorblind-safer). */
export const DIVERGING_PALETTE_PURPLE_GREEN: readonly string[] = [
  'var(--data-viz-div-purpleGreen-low, #A77AAB)',
  'var(--data-viz-div-purpleGreen-neutral, #F8FAFC)',
  'var(--data-viz-div-purpleGreen-high, #4C9950)',
] as const;

/** Registry of sequential families keyed by name (light-mode tokens). */
export const SEQUENTIAL_PALETTES: Record<SequentialPalette, readonly string[]> =
  {
    blues: SEQUENTIAL_PALETTE_BLUES,
    greens: SEQUENTIAL_PALETTE_GREENS,
    oranges: SEQUENTIAL_PALETTE_ORANGES,
    purples: SEQUENTIAL_PALETTE_PURPLES,
  };

/** Registry of diverging families keyed by name (light-mode tokens). */
export const DIVERGING_PALETTES: Record<DivergingPalette, readonly string[]> = {
  blueOrange: DIVERGING_PALETTE_BLUE_ORANGE,
  purpleGreen: DIVERGING_PALETTE_PURPLE_GREEN,
};

/**
 * Dark-mode stop values for each sequential family. These are the raw hexes
 * published as `.dark` overrides of `--data-viz-seq-*` in `tailwind.config.ts`;
 * exported so the accessibility test can verify them directly (the runtime
 * resolver never reads these — the CSS var override swaps them in).
 */
export const SEQUENTIAL_PALETTES_DARK: Record<
  SequentialPalette,
  readonly string[]
> = {
  blues: ['#3879B9', '#72A0CD', '#ACC7E2', '#ECF2F8'],
  greens: ['#358638', '#6AAA6D', '#A8CDA9', '#EBF3EB'],
  oranges: ['#AC6418', '#E28520', '#F0BA7F', '#FCEFE2'],
  purples: ['#97639C', '#B58FB8', '#D3BDD5', '#F5F0F5'],
};

/** Dark-mode stop values for each diverging family (`[low, neutral, high]`). */
export const DIVERGING_PALETTES_DARK: Record<
  DivergingPalette,
  readonly string[]
> = {
  blueOrange: ['#F0BA7F', '#282A2E', '#ACC7E2'],
  purpleGreen: ['#D3BDD5', '#282A2E', '#A8CDA9'],
};

/**
 * Extract the `#hex` fallback from a `var(--token, #hex)` palette string, or
 * return the input unchanged when it is already a bare color. Used by consumers
 * that feed colors to engines which cannot parse CSS `var(...)` (e.g. d3-scale
 * interpolation in HeatmapChart).
 */
export function getHexFallback(token: string): string {
  const match = /#[0-9a-fA-F]{3,8}/.exec(token);
  return match ? match[0] : token;
}

/** Default sequential family when `sequentialPalette` is omitted. */
export const DEFAULT_SEQUENTIAL_PALETTE: SequentialPalette = 'blues';
/** Default diverging family when `divergingPalette` is omitted. */
export const DEFAULT_DIVERGING_PALETTE: DivergingPalette = 'blueOrange';

/** Family names available for the sequential scale, in registry order. */
export const SEQUENTIAL_PALETTE_FAMILIES = Object.keys(
  SEQUENTIAL_PALETTES,
) as SequentialPalette[];
/** Family names available for the diverging scale, in registry order. */
export const DIVERGING_PALETTE_FAMILIES = Object.keys(
  DIVERGING_PALETTES,
) as DivergingPalette[];

/**
 * Resolve a sequential family name to its ordered stop array. Unknown names
 * fall back to {@link DEFAULT_SEQUENTIAL_PALETTE} with a dev warning — the
 * `family` arg is typed loosely because operator-authored values arrive
 * untyped at runtime.
 */
export function getSequentialPalette(
  family?: string | null,
): readonly string[] {
  if (
    family != null &&
    Object.prototype.hasOwnProperty.call(SEQUENTIAL_PALETTES, family)
  ) {
    return SEQUENTIAL_PALETTES[family as SequentialPalette];
  }
  if (family != null && family !== '') {
    warnOnce(
      `chart-palette/unknown-sequential-family:${family}`,
      `[hen-ui/chart-palette] Unknown sequential palette family "${family}" — falling back to "${DEFAULT_SEQUENTIAL_PALETTE}".`,
    );
  }
  return SEQUENTIAL_PALETTES[DEFAULT_SEQUENTIAL_PALETTE];
}

/**
 * Resolve a diverging family name to its `[low, neutral, high]` stop array.
 * Unknown names fall back to {@link DEFAULT_DIVERGING_PALETTE} with a warning.
 */
export function getDivergingPalette(family?: string | null): readonly string[] {
  if (
    family != null &&
    Object.prototype.hasOwnProperty.call(DIVERGING_PALETTES, family)
  ) {
    return DIVERGING_PALETTES[family as DivergingPalette];
  }
  if (family != null && family !== '') {
    warnOnce(
      `chart-palette/unknown-diverging-family:${family}`,
      `[hen-ui/chart-palette] Unknown diverging palette family "${family}" — falling back to "${DEFAULT_DIVERGING_PALETTE}".`,
    );
  }
  return DIVERGING_PALETTES[DEFAULT_DIVERGING_PALETTE];
}

/**
 * Sample `count` evenly-spaced stops from an ordered palette `stops` array.
 *
 * - `count <= 0` → `[]`.
 * - `count === 1` → the mid-range stop (single-series sequential charts read
 *   as one representative mid-tone rather than the lightest/darkest extreme).
 * - `count >= 2` → `count` stops spread evenly across the full ramp by index.
 *   When `count` exceeds the number of stops the tail repeats — sequential is
 *   intended for low series counts; use `categorical` for many series.
 */
export function sampleSequentialStops(
  stops: readonly string[],
  count: number,
): string[] {
  if (count <= 0 || stops.length === 0) return [];
  const midIndex = Math.floor((stops.length - 1) / 2);
  if (count === 1) return [stops[midIndex]];
  const lastIndex = stops.length - 1;
  return Array.from({ length: count }, (_, i) => {
    const idx = Math.round((i * lastIndex) / (count - 1));
    return stops[Math.min(idx, lastIndex)];
  });
}

/**
 * Resolve the diverging arm color for a single data-point value, anchored at
 * zero: `value >= 0` → high arm, `value < 0` → low arm. Returns `undefined`
 * for `null` / non-finite values so callers can fall back to the series base
 * color (mirrors the `auto-by-value` per-cell path).
 */
export function resolveDivergingArmColor(
  value: number | null,
  family?: string | null,
): string | undefined {
  if (value === null || !Number.isFinite(value)) return undefined;
  const stops = getDivergingPalette(family);
  return value >= 0 ? stops[stops.length - 1] : stops[0];
}

/** Normalize an untyped `colorScale` value, warning on unknown inputs. */
function normalizeColorScale(scale?: string | null): ColorScale {
  if (
    scale === 'categorical' ||
    scale === 'sequential' ||
    scale === 'diverging'
  ) {
    return scale;
  }
  if (scale != null && scale !== '') {
    warnOnce(
      `chart-palette/unknown-color-scale:${scale}`,
      `[hen-ui/chart-palette] Unknown colorScale "${scale}" — falling back to "categorical".`,
    );
  }
  return 'categorical';
}

/** Shared "fill missing color" pass used by the color-scale resolver. */
function fillSeriesColors(
  config: ChartConfig,
  series: readonly string[],
  colorFor: (key: string) => string,
): ChartConfig {
  const next: ChartConfig = { ...config };
  series.forEach((key) => {
    const entry = next[key];
    const hasResolvedColor =
      entry !== undefined && (entry.color !== undefined || 'theme' in entry);
    if (hasResolvedColor) return;
    next[key] = {
      label: entry?.label ?? key,
      icon: entry?.icon,
      color: colorFor(key),
    };
  });
  return next;
}

/** Options for {@link withSeriesColorScaleDefaults}. */
export interface ColorScaleDefaultsOptions {
  /** Color-scale mode (default `'categorical'`). Unknown values warn + fall back. */
  colorScale?: string | null;
  /** Sequential family (default `'blues'`). Unknown values warn + fall back. */
  sequentialPalette?: string | null;
  /** Diverging family (default `'blueOrange'`). Unknown values warn + fall back. */
  divergingPalette?: string | null;
  /**
   * Series currently hidden via the interactive legend. Excluded from the
   * sequential gradient spread so the remaining visible series span the full
   * ramp (the hidden-series interaction). Ignored for `categorical` /
   * `diverging`.
   */
  hiddenSeries?: ReadonlySet<string>;
}

/**
 * Palette-source-aware replacement for {@link withSeriesPaletteDefaults}.
 * Fills each series' default `color` based on the chosen `colorScale`:
 *
 * - `categorical` (default) → delegates to {@link withSeriesPaletteDefaults},
 *   so output is byte-identical to historical behavior.
 * - `sequential` → spreads evenly-sampled stops of the chosen family across
 *   the **visible** series in order; a single visible series gets the
 *   mid-range stop (+ dev `info`).
 * - `diverging` → seeds each series' base color with the family's high arm
 *   (the per-data-point sign mapping is applied via `<Cell>` fills in the
 *   composites, parallel to the `auto-by-value` path).
 *
 * Explicit `config[key].color` / `theme` always passes through untouched
 * (override wins). The returned object is a fresh clone — the input is never
 * mutated.
 */
export function withSeriesColorScaleDefaults(
  config: ChartConfig,
  series: readonly string[],
  options: ColorScaleDefaultsOptions = {},
): ChartConfig {
  const scale = normalizeColorScale(options.colorScale);

  if (scale === 'categorical') {
    return withSeriesPaletteDefaults(config, series);
  }

  if (scale === 'diverging') {
    const stops = getDivergingPalette(options.divergingPalette);
    const highArm = stops[stops.length - 1];
    return fillSeriesColors(config, series, () => highArm);
  }

  // sequential
  const stops = getSequentialPalette(options.sequentialPalette);
  const visible = options.hiddenSeries
    ? series.filter((key) => !options.hiddenSeries?.has(key))
    : series;
  if (visible.length === 1) {
    infoOnce(
      'chart-palette/sequential-single-series',
      '[hen-ui/chart-palette] colorScale="sequential" with a single visible series renders the mid-range palette stop. Use "categorical" for a single-series chart if a distinct hue is preferred.',
    );
  }
  const sampled = sampleSequentialStops(stops, visible.length);
  const colorByKey = new Map<string, string>();
  visible.forEach((key, index) => colorByKey.set(key, sampled[index]));
  const fallbackStop = stops[Math.floor((stops.length - 1) / 2)];
  return fillSeriesColors(
    config,
    series,
    (key) => colorByKey.get(key) ?? fallbackStop,
  );
}
